<script setup lang="ts">
import { ref, computed, h } from 'vue';
import { getCurrentLocale } from '@/i18n';
import { useI18n } from 'vue-i18n';
import { DateTime } from 'luxon';
import { STATUS_CLOSED, STATUS_OPEN, LANG_ENGLISH } from '@/constants';
import { useDestinationStore } from '@/store/DestinationStore';
import { useHotelStore } from '@/store/HotelStore';
import { formatDate } from '@/utils/dateUtils';
import { getTextVariant } from '@/utils/stringUtils';
import { useTabComposable } from '../useTabComposable';
import { useDialog } from 'primevue/usedialog';
import AutoComplete from 'primevue/autocomplete';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import Fluid from 'primevue/fluid';
import Select from 'primevue/select';
import ModalSelectDate from '@/components/modal/ModalSelectDate.vue';
import RoomCategoryFetchingModal from '../RoomCategoryFetchingModal.vue';
import ValidateQuoteFormNotification from '@/components/modal/ValidateQuoteFormNotification.vue';

type DateRange = Date[] | string[] | undefined;
interface Props {
  advancedForm: object;
  packageData?: any;
  indexOption?: number;
  validate?: any;
  isShowError?: boolean;
  minDateRangeValue?: DateRange;
  maxDateRangeValue?: DateRange;
  durationDate: any;
  dateValue?: any;
  maxHotelConfig: number;
  maxRoomConfig: number;
}
const props = defineProps<Props>();

const emits = defineEmits<{
  removeHotelPackage: [indexOption: number];
  addHotelPackage: [indexOption: number];
  removeRoomPackage: [indexOption: number, indexHotel: number];
  addRoomPackage: [indexOption: number, indexHotel: number];
  updateDateValue: [event: any, index: number, data: any];
  updateDurationDate: [event: any, index: number, data: any];
  'update:packageData': [value: any];
}>();

const packageDataForm = computed({
  get() {
    return props.packageData;
  },
  set(value) {
    emits('update:packageData', value);
  }
});

const { hotelRoomPayload, filterHotel, roomCategoryFetchingModalControl, getRoomCategoriesData, filterRoomOptions, disableAddRoom, depDateForm, checkIsAfterDate346 } = useTabComposable();

const { t } = useI18n({ useScope: 'global' });
const dialog = useDialog();

const currentLanguage = getCurrentLocale();
const hotelRoomCategoryData = new Map();
const destinationStore = useDestinationStore();
const hotelStore = useHotelStore();

const getRoomsLoadings = ref([[]]);
const filterPackageFullHotel = ref();
const today = new Date();
const currentDay = today.getDate();
today.setDate(currentDay + 346);

const packageInstaStatus = ref([[STATUS_OPEN]]);
const hotelMultiple = computed(() => hotelStore.selectHotelMultiple);
const destinationSelected = computed(() => destinationStore.selectDestinationSelected);
const hotelRoomCategoryOptions = computed(() => hotelRoomCategoryData);

const handleOpenModalDate = (listDate: any, callBackFn: any) => {
  const dialogRef = dialog.open(ModalSelectDate, {
    data: {
      availableDates: listDate
    },
    props: {
      modal: true,
      header: t('advance_quote.alternative_date'),
      style: {
        width: '560px'
      }
    },
    emits: {
      isSave: (dateSelected: any) => {
        dialogRef.close();
        callBackFn(dateSelected);
      }
    }
  });
};

const handleOpenModalError = (isAfterDate346 = false) => {
  const modalRef = dialog.open(ValidateQuoteFormNotification, {
    data: {
      message: isAfterDate346 ? t('notification.both_land_package_plus_error') : t('notification.over_departure_date'),
      isAlignCenter: true
    },
    props: {
      header: t('advance_quote.form.notice'),
      modal: true
    },
    templates: {
      footer: () => {
        return [h('div', { class: 'text-center' }, h(Button, { autofocus: true, label: t('common.buttons.close'), onClick: () => modalRef.close() }))];
      }
    }
  });
};

const addHotelPackage = (index: number) => {
  emits('addHotelPackage', index);
};

const removeHotelPackage = (index: number) => {
  emits('removeHotelPackage', index);
};

const addRoomPackage = (index: number, indexHotel: number) => {
  emits('addRoomPackage', index, indexHotel);
};

const removeRoomPackage = (index: number, indexHotel: number) => {
  emits('removeRoomPackage', index, indexHotel);
};

const renderNewDepDatePackage = (depDate, packageIndex, response) => {
  if (response) {
    packageDataForm.value[packageIndex].dateRange = new Date(DateTime.fromISO(depDate));
    emits('updateDateValue', packageDataForm.value[packageIndex].dateRange, packageIndex, packageDataForm.value);
    emits('updateDurationDate', { value: packageDataForm.value[packageIndex].duration }, packageIndex, packageDataForm.value);
  }
};

const getListRoomPackageData = async (payload, packageIndex: number, hotelIndex: number, hotelSelects, isAfterDate346 = false) => {
  getRoomsLoadings.value[packageIndex][hotelIndex] = true;
  const gateway = packageDataForm.value[packageIndex].gateway;
  if (!hotelSelects || typeof hotelSelects !== 'object') {
    return;
  }
  hotelSelects.groupDes = destinationSelected?.value?.groupDes;
  checkIsAfterDate346(isAfterDate346);
  const limitData = {
    min: props.minDateRangeValue?.[0],
    max: props.maxDateRangeValue?.[0],
    isPackageForm: true
  };
  const response = await getRoomCategoriesData(payload, hotelSelects, hotelSelects?.excludeHotelRoom, gateway, handleOpenModalDate, handleOpenModalError, limitData);
  if (response === null) {
    getRoomsLoadings.value[packageIndex][hotelIndex] = false;
    return;
  }

  renderNewDepDatePackage(depDateForm.value, packageIndex, response);
  if (response?.data?.length) {
    if (hotelSelects?.excludeHotelRoom) {
      const listRoom = response.data?.filter((room) => !hotelSelects?.excludeHotelRoom.map((excludeRoom) => excludeRoom.toLowerCase()).includes(room.room.toLowerCase()));
      hotelRoomCategoryData.set(payload.productCode, listRoom);
    } else {
      hotelRoomCategoryData.set(payload.productCode, response.data);
    }
  }
  if (!hotelRoomCategoryData.get(payload.productCode)?.length) {
    packageDataForm.value[packageIndex].hotels[hotelIndex].rooms.length = 1;
  }

  getRoomsLoadings.value[packageIndex][hotelIndex] = false;
};

const searchHotelPackage = async (packageFullEvent, index: number) => {
  let hotelPackageList = [...hotelMultiple.value[index]];
  let hotelCodes: string[] = [];
  packageDataForm.value.forEach((item: any) => {
    const codes = item.hotels.filter((item: any) => item.hotel && item.hotel.hCode).map((hotel: any) => hotel.hotel.hCode);
    hotelCodes = hotelCodes.concat(codes);
  });
  if (props.advancedForm[0].groupTypeId) {
    hotelPackageList = hotelPackageList.filter((hotel) => hotel.groupTypes.includes(props.advancedForm[0].groupTypeId));
    hotelPackageList = hotelPackageList.filter((item: any) => !hotelCodes.includes(item.hCode));
  }
  filterPackageFullHotel.value = filterHotel(packageFullEvent, hotelPackageList);
};

const eventHotelPackageChanged = async (e, index, hotelIndex) => {
  if (!packageInstaStatus.value[index]) {
    packageInstaStatus.value[index] = [];
  }
  packageInstaStatus.value[index][hotelIndex] = e.value?.insta || STATUS_CLOSED;

  packageDataForm.value[index].hotels[hotelIndex].rooms = clearHotelRoomPackage(packageDataForm.value[index].hotels[hotelIndex].rooms);
  packageDataForm.value.forEach((packageItem) => {
    packageItem.hotels[hotelIndex].hotel = e.value;
  });
  const payload = {
    dateDep: formatDate(packageDataForm.value[index].dateRange, 'yyyyLLdd'),
    dateRet: formatDate(props.durationDate?.[index] ? props.durationDate?.[index] : props.dateValue?.[index], 'yyyyLLdd'),
    duration: packageDataForm.value[index].duration.name,
    destDep: packageDataForm.value[index].destination?.airportCode,
    productCode: e.value?.hCode,
    language: currentLanguage.toUpperCase(),
    option: 'PACKAGE',
    nbAdult: packageDataForm.value[index].adults || 0,
    ...hotelRoomPayload
  };
  const isAfterDate346 = new Date(packageDataForm.value[index].dateRange).getTime() > today.getTime();
  await getListRoomPackageData(payload, index, hotelIndex, e.value, isAfterDate346);
};

const eventRoomPackageChanged = (e, hotelIndex, roomIndex) => {
  packageDataForm.value.forEach((packageItem) => {
    packageItem.hotels[hotelIndex].rooms[roomIndex].room = e.value;
  });
};

const clearHotelPackage = (hotel) => {
  hotel.hotel = '';
  hotel.rooms = clearHotelRoomPackage(hotel.rooms);
  hotelRoomCategoryData.clear();
};

const clearHotelRoomPackage = (rooms) => {
  return rooms.map(() => {
    return {
      room: ''
    };
  });
};
</script>
<template>
  <div class="package-hotel-room">
    <Fluid v-for="(hotel, indexHotel) in packageDataForm[indexOption].hotels" :key="indexHotel">
      <div class="field">
        <label for="hotel" class="long-text" v-tooltip.top="$t('advance_quote.form.hotel') + ' ' + (indexHotel + 1)"> {{ $t('advance_quote.form.hotel') }} {{ indexHotel + 1 }} </label>
        <div class="grid align-center">
          <div class="col-12 pb-0 flex align-center justify-between">
            <div class="flex align-items-center w-full">
              <AutoComplete
                v-model="hotel.hotel"
                completeOnFocus
                :suggestions="filterPackageFullHotel"
                :disabled="!packageDataForm[indexOption].destination || !packageDataForm[indexOption].duration?.name || !packageDataForm[indexOption].dateRange || !props.advancedForm[0].groupTypeId || indexOption !== 0"
                :optionLabel="currentLanguage === LANG_ENGLISH ? 'name' : 'nameFr'"
                :placeholder="$t('advance_quote.form.select_hotel')"
                @complete="searchHotelPackage($event, indexOption)"
                @change="eventHotelPackageChanged($event, indexOption, indexHotel)"
                class="w-full"
                forceSelection
                :class="isShowError && validate.hotels[0]?.$response.$errors[indexHotel].hotel[0]?.$message ? 'p-invalid w-full' : 'w-full'"
              >
                <template #option="slotProps">
                  <div class="flex align-options-center">
                    <div>
                      <div>{{ currentLanguage === LANG_ENGLISH ? `${slotProps.option.name} (${slotProps.option.destName})` : `${slotProps.option.nameFr} (${slotProps.option.destNameFr})` }}</div>
                    </div>
                  </div>
                </template>
              </AutoComplete>
              <div v-if="indexOption === 0">
                <i id="clear-package-hotel" class="pi pi-times-circle ml-2 text-red-500 cursor-pointer" @click="clearHotelPackage(hotel)"></i>
              </div>
            </div>
          </div>
          <div class="ml-2">
            <span class="text-red-500" v-if="isShowError">
              {{ validate?.hotels[0]?.$response.$errors[indexHotel].hotel[0]?.$message }}
            </span>
          </div>
        </div>
        <div class="grid" v-for="(room, indexRoom) in hotel.rooms" :key="indexRoom">
          <div class="col-12 pb-0 flex align-center justify-between">
            <Select
              id="room-selection"
              v-model="room.room"
              :disabled="!hotel.hotel || indexOption !== 0"
              :options="filterRoomOptions(hotelRoomCategoryOptions.get(hotel.hotel?.hCode), hotel.rooms, true)"
              :optionLabel="getTextVariant(currentLanguage, 'roomDescription', 'roomDescriptionFr')"
              :placeholder="$t('advance_quote.form.select_room')"
              :emptyMessage="$t('common.no_available_option')"
              @change="eventRoomPackageChanged($event, indexHotel, indexRoom)"
              :loading="getRoomsLoadings?.[indexOption]?.[indexHotel]"
              :class="isShowError && validate?.hotels[0]?.$response.$errors[indexHotel].rooms[0]?.$response.$errors[indexRoom]?.room[0]?.$message ? 'p-invalid w-full mt-2' : 'w-full mt-2'"
            >
              <template #value="{ value, placeholder }">
                <div v-if="value">
                  {{ getTextVariant(currentLanguage, value.roomDescription, value.roomDescriptionFr) }}
                  <span v-if="value.meal">-{{ value.meal }}</span>
                </div>
                <div v-else>{{ placeholder }}</div>
              </template>
              <template #option="slotProps">
                <div>
                  {{ getTextVariant(currentLanguage, slotProps.option.roomDescription, slotProps.option.roomDescriptionFr) }}
                  <span v-if="slotProps.option.meal">- {{ slotProps.option.meal }}</span>
                </div>
              </template>
            </Select>
          </div>
          <div class="ml-2">
            <span class="text-red-500" v-if="isShowError">
              {{ validate?.hotels[0]?.$response.$errors[0].rooms[0]?.$response.$errors[indexHotel]?.room[0]?.$message }}
            </span>
          </div>
        </div>
        <div class="grid mt-3">
          <div class="flex w-full justify-content-around mb-3 mt-3">
            <div id="remove-package-room" class="block text-primary cursor-pointer" @click="removeRoomPackage(indexOption, indexHotel, hotel.rooms.length - 1)" v-if="hotel.rooms.length > 1 && indexOption === 0">
              - {{ $t('advance_quote.form.remove_room') }}
            </div>
            <div
              id="add-package-room"
              v-if="indexOption === 0 && hotel.rooms?.length < maxRoomConfig"
              class="block text-primary add-room-button"
              :class="[disableAddRoom(hotelRoomCategoryOptions.get(hotel.hotel?.hCode), hotel.rooms) && 'disabled']"
              @click="() => !disableAddRoom(hotelRoomCategoryOptions.get(hotel.hotel?.hCode), hotel.rooms) && addRoomPackage(indexOption, indexHotel)"
            >
              + {{ $t('advance_quote.form.add_room') }}
            </div>
          </div>
        </div>
      </div>
    </Fluid>
    <div class="grid">
      <div class="flex w-full justify-content-around mb-3">
        <div id="remove-package-hotel" class="block text-primary cursor-pointer" @click="removeHotelPackage(indexOption)" v-if="packageDataForm[indexOption].hotels.length > 1 && indexOption === 0">- {{ $t('advance_quote.form.remove_hotel') }}</div>
        <div id="add-package-hotel" class="block text-primary cursor-pointer" v-if="indexOption === 0 && packageDataForm[indexOption].hotels.length < maxHotelConfig" @click="addHotelPackage(indexOption)">
          + {{ $t('advance_quote.form.add_hotel') }}
        </div>
      </div>
    </div>
  </div>
  <Dialog v-model:visible="roomCategoryFetchingModalControl.visible" modal :header="roomCategoryFetchingModalControl.isFetching ? $t('advance_quote.searching_for_room') : $t('advance_quote.no_rooms_available')">
    <RoomCategoryFetchingModal :is-fetching="roomCategoryFetchingModalControl.isFetching" @closeModal="roomCategoryFetchingModalControl.visible = false"></RoomCategoryFetchingModal>
  </Dialog>
</template>
